import { XIcon } from '@heroicons/react/outline';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import { tone } from '@/components/agents/primitives';

import { useTemplateTranslation } from './kit';
import type { TemplateSampleSet } from './sampleSets';

/**
 * Finished examples for the templates that make something visual (cards,
 * worksheets, carousels), shown in the workspace's output pane. Click one to
 * see it full size.
 */
export default function TemplateSamples({ set }: { set: TemplateSampleSet }) {
  const { t } = useTemplateTranslation();
  const [open, setOpen] = useState<{ src: string; alt: string } | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Escape closes; the page doesn't scroll behind the picture.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(null);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const count = set.samples.length;

  return (
    <>
      <ul
        className={clsxm(
          'grid gap-3',
          count === 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3',
        )}
      >
        {set.samples.map((sample) => {
          const src = `${set.baseUrl}/${sample.file}`;
          const label = t(sample.label);
          const alt = sample.alt || sample.label;
          return (
            <li key={sample.file}>
              <button
                type='button'
                onClick={() => setOpen({ src, alt })}
                aria-label={`${t('Preview')} ${label} ${t(set.ariaLabel)}`}
                className={clsxm(
                  'group block w-full cursor-zoom-in overflow-hidden rounded-xl text-start transition duration-200 hover:ring-[#0f0f28]/[0.16] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:ring-white/20',
                  tone.surface,
                  tone.line,
                )}
              >
                <span
                  className={clsxm(
                    'relative block w-full overflow-hidden',
                    set.aspectClassName,
                    set.frameClassName,
                  )}
                >
                  <Image
                    src={src}
                    alt={alt}
                    fill
                    className={set.imageClassName}
                    sizes='(max-width: 640px) 33vw, (max-width: 1024px) 25vw, 180px'
                  />
                </span>
                <span
                  className={clsxm(
                    'block truncate border-t border-[#0f0f28]/[0.06] px-2 py-2 text-center text-[12px] font-medium dark:border-white/[0.06]',
                    tone.body,
                  )}
                >
                  {label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {open ? (
        <div
          role='dialog'
          aria-modal='true'
          aria-label={open.alt}
          onClick={() => setOpen(null)}
          className='fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-[#0b0b13]/85 p-4 backdrop-blur-sm'
        >
          <button
            ref={closeRef}
            type='button'
            onClick={() => setOpen(null)}
            aria-label={t('Close')}
            className='absolute end-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition hover:bg-white/20'
          >
            <XIcon className='h-5 w-5' aria-hidden='true' />
          </button>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={open.src}
            alt={open.alt}
            className='max-h-[92vh] max-w-[94vw] rounded-2xl shadow-2xl'
          />
        </div>
      ) : null}
    </>
  );
}
